import * as THREE from "three";
import { GLTFLoader } from "./gltf-loader.js";

const container = document.querySelector("#model-wrap");
const heroModel = document.querySelector(".hero-model");
const heroSection = document.querySelector("#hero");
const caseFileSection = document.querySelector("#case-file");
const caseModelTarget = document.querySelector("#case-model-wrap");
const caseOverlayTags = [...document.querySelectorAll(".case-file__note, .case-file__warning")];
const caseRotateButton = document.querySelector("[data-case-rotate]");
const flavorLabel = document.querySelector("#flavor-label");
const flavorButtons = [...document.querySelectorAll("[data-direction]")];
const heroCta = heroSection?.querySelector(".hero-cta");

function getContainerSize() {
  const rect = container.getBoundingClientRect();
  return {
    width: Math.max(1, rect.width || window.innerWidth),
    height: Math.max(1, rect.height || window.innerHeight),
  };
}

const productOrder = ["sour", "original"];
const products = {
  original: {
    label: "Original",
    url: window.TwoFourSixAssets.pouchOriginal,
  },
  sour: {
    label: "Sour",
    url: window.TwoFourSixAssets.pouchSour,
  },
};

const flavorColors = {
  original: "#F6BE3B",
  sour: "#AECC3F",
};

const scene = new THREE.Scene();

const initialSize = getContainerSize();
const camera = new THREE.PerspectiveCamera(32, initialSize.width / initialSize.height, 0.01, 100);
camera.position.set(0, 0.08, 2.45);

const renderer = new THREE.WebGLRenderer({
  antialias: true,
  alpha: true,
  powerPreference: "high-performance",
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(initialSize.width, initialSize.height);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.NoToneMapping;
container.appendChild(renderer.domElement);

const ambient = new THREE.AmbientLight(0xffffff, 1.45);
scene.add(ambient);

const key = new THREE.DirectionalLight(0xffffff, 2.2);
key.position.set(1.6, 1.8, 2.3);
scene.add(key);

const rim = new THREE.DirectionalLight(0xffffff, 0.65);
rim.position.set(-1.8, 0.9, -1.5);
scene.add(rim);

const loader = new GLTFLoader();
const loadedProducts = new Map();
const root = new THREE.Group();
scene.add(root);

let currentProduct = "sour";
let activeModel = null;
let isSwitching = false;
let flipStart = 0;
let flipFrom = 0;
let flipTo = 0;
let flipDirection = 1;
let pendingProduct = null;
let pendingModel = null;
let swappedDuringFlip = false;

const FLIP_DURATION = 950;
const FLIP_RADIANS = Math.PI;
const FULL_TURN_RADIANS = Math.PI * 2;
const CASE_SCROLL_ROOT_YAW = FULL_TURN_RADIANS - 0.02;
const CASE_ROTATION_EPSILON = 0.002;
const SWAP_PROGRESS = 0.5;
const TILT_PITCH = 0.07;
const TILT_YAW = 0.22;
const TILT_ROLL = 0.18;
const baseRotation = new THREE.Euler(0.02, -0.18, 0);
const mouse = { x: 0, y: 0 };
const tilt = { x: 0, y: 0 };
const scrollMotion = {
  progress: 0,
  eased: 0,
  active: false,
};
let scrollLayout = null;
const tagOverlays = new Map();
const caseRotation = {
  current: 0,
  target: 0,
  side: 0,
};

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function clamp(value, min = 0, max = 1) {
  return Math.min(Math.max(value, min), max);
}

function lerp(from, to, progress) {
  return from + (to - from) * progress;
}

function normalizePositiveRadians(radians) {
  return ((radians % FULL_TURN_RADIANS) + FULL_TURN_RADIANS) % FULL_TURN_RADIANS;
}

function hasCaseRotation() {
  return caseRotation.side === 1 || caseRotation.current > CASE_ROTATION_EPSILON || caseRotation.target > CASE_ROTATION_EPSILON;
}

function getCaseLocalSpin(eased) {
  if (!hasCaseRotation()) return 0;
  return (caseRotation.current - CASE_SCROLL_ROOT_YAW) * eased;
}

function prepareModel(model) {
  model.traverse((child) => {
    if (!child.isMesh) return;
    child.castShadow = false;
    child.receiveShadow = false;
    if (child.material) {
      child.material.side = THREE.FrontSide;
      child.material.color?.set(0xffffff);
      child.material.roughness = 0.58;
      child.material.metalness = 0;
    }
  });

  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDimension = Math.max(size.x, size.y, size.z);
  const scale = 1.22 / maxDimension;

  model.position.sub(center);

  const group = new THREE.Group();
  group.add(model);
  group.scale.setScalar(scale);
  group.visible = false;
  return group;
}

function loadProduct(key) {
  if (loadedProducts.has(key)) {
    return Promise.resolve(loadedProducts.get(key));
  }

  return new Promise((resolve, reject) => {
    loader.load(
      products[key].url,
      (gltf) => {
        const model = prepareModel(gltf.scene);
        loadedProducts.set(key, model);
        root.add(model);
        resolve(model);
      },
      undefined,
      reject,
    );
  });
}

function setButtonsDisabled(disabled) {
  flavorButtons.forEach((button) => {
    button.disabled = disabled;
  });
}

function updateHeroFlavorState(product, { animate = true } = {}) {
  const color = flavorColors[product] || flavorColors.original;
  const isSour = product === "sour";
  heroSection?.classList.toggle("is-sour-flavor", product === "sour");
  caseFileSection?.classList.toggle("is-sour-flavor", isSour);
  document.body?.classList.toggle("is-sour-flavor", isSour);
  window.dispatchEvent(new CustomEvent("pouch-flavor-change", { detail: { product } }));

  if (!heroSection || !heroCta) return;

  if (!animate) {
    heroSection.classList.remove("is-hero-color-wiping");
    heroCta.style.setProperty("--hero-cta-bg", color);
    heroCta.style.setProperty("--hero-cta-wipe", color);
    return;
  }

  heroSection.classList.remove("is-hero-color-wiping");
  heroCta.style.setProperty("--hero-cta-wipe", color);
  void heroSection.offsetWidth;
  heroSection.classList.add("is-hero-color-wiping");

  const finishWipe = (event) => {
    if (event.animationName !== "heroCtaColorWipe") return;
    heroCta.style.setProperty("--hero-cta-bg", color);
    heroSection.classList.remove("is-hero-color-wiping");
    heroCta.removeEventListener("animationend", finishWipe);
  };

  heroCta.addEventListener("animationend", finishWipe);
}

function warmUpProductModels() {
  const originalVisibility = new Map();

  loadedProducts.forEach((model) => {
    originalVisibility.set(model, model.visible);
    model.visible = true;
    model.updateMatrixWorld(true);

    model.traverse((child) => {
      if (!child.isMesh) return;
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material) => {
        if (!material) return;
        ["map", "normalMap", "roughnessMap", "metalnessMap", "alphaMap", "emissiveMap"].forEach((key) => {
          const texture = material[key];
          if (texture && renderer.initTexture) {
            renderer.initTexture(texture);
          }
        });
      });
    });
  });

  root.updateMatrixWorld(true);
  renderer.compile(scene, camera);
  renderer.render(scene, camera);

  loadedProducts.forEach((model) => {
    model.visible = originalVisibility.get(model);
  });
}

function whenIntroDone(callback) {
  const intro = document.querySelector("[data-site-intro]");
  if (!intro) {
    callback();
    return;
  }
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    callback();
  };
  window.addEventListener("site-intro-done", finish, { once: true });
  // Safety net in case the intro is removed without firing its event.
  window.setTimeout(finish, 5000);
}

function runIdle(callback, timeout = 1500) {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout });
  } else {
    window.setTimeout(callback, 200);
  }
}

async function showInitialProduct() {
  // Only load the product that's shown first so the opening animation isn't
  // fighting the parse/compile of every model at once.
  await loadProduct(currentProduct);
  activeModel = loadedProducts.get(currentProduct);
  activeModel.visible = true;
  updateHeroFlavorState(currentProduct, { animate: false });
  if (flavorLabel) flavorLabel.textContent = products[currentProduct].label;
  window.__pouchModelReady = true;
  window.dispatchEvent(new CustomEvent("pouch-model-ready"));
  setButtonsDisabled(false);

  // Pull in the remaining models and warm up shaders only once the intro is
  // gone and the main thread is idle, so neither competes with the animation.
  whenIntroDone(() => {
    runIdle(() => {
      const others = productOrder.filter((product) => product !== currentProduct);
      Promise.all(others.map((product) => loadProduct(product)))
        .catch(() => {})
        .finally(() => runIdle(() => warmUpProductModels()));
    });
  });
}

async function switchProduct(nextProduct, direction = 1) {
  if (isSwitching || nextProduct === currentProduct) return;

  setButtonsDisabled(true);
  flipDirection = direction >= 0 ? 1 : -1;
  pendingProduct = nextProduct;
  pendingModel = await loadProduct(nextProduct);
  pendingModel.rotation.y = 0;
  pendingModel.visible = false;
  swappedDuringFlip = false;
  isSwitching = true;
  flipStart = performance.now();
  flipFrom = root.rotation.y;
  flipTo = flipFrom + FLIP_RADIANS * flipDirection;
}

flavorButtons.forEach((button) => {
  button.addEventListener("click", () => {
    const direction = Number(button.dataset.direction);
    const currentIndex = productOrder.indexOf(currentProduct);
    const nextIndex = (currentIndex + direction + productOrder.length) % productOrder.length;
    const nextProduct = productOrder[nextIndex];
    updateHeroFlavorState(nextProduct);
    switchProduct(nextProduct, direction);
  });
});

caseRotateButton?.addEventListener("click", () => {
  // Flip the pouch one half-turn at a time, but keep the stored angle bounded
  // so the scroll-linked return to the hero never replays accumulated spins.
  caseRotation.side = caseRotation.side === 0 ? 1 : 0;
  caseRotateButton.setAttribute("aria-pressed", String(caseRotation.side === 1));
  caseRotation.current = normalizePositiveRadians(caseRotation.current);
  caseRotation.target = caseRotation.side === 1 ? Math.PI : FULL_TURN_RADIANS;
});

window.addEventListener("pointermove", (event) => {
  const rect = container.getBoundingClientRect();
  const width = Math.max(1, rect.width);
  const height = Math.max(1, rect.height);
  mouse.x = ((event.clientX - rect.left) / width - 0.5) * 2;
  mouse.y = ((event.clientY - rect.top) / height - 0.5) * 2;
});

function resize() {
  const { width, height } = getContainerSize();
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);
  scrollLayout = null;
  syncTagOverlays(false);
}

window.addEventListener("resize", resize);

function measureScrollLayout() {
  if (!heroModel || !caseFileSection || !caseModelTarget) return null;
  const modelStyles = getComputedStyle(heroModel);
  const modelTop = Number.parseFloat(modelStyles.top) || 0;
  const modelHeight = Number.parseFloat(modelStyles.height) || 560;
  const heroTop = heroSection ? heroSection.getBoundingClientRect().top + window.scrollY : 0;
  const caseTop = caseFileSection.getBoundingClientRect().top + window.scrollY;
  const targetRect = caseModelTarget.getBoundingClientRect();
  const startScroll = Math.max(0, heroTop + modelTop - window.innerHeight * 0.2);
  const caseLandingFactor = window.innerWidth < 900 ? 0.56 : window.innerWidth < 1180 ? 0.48 : 0.34;
  const endScroll = Math.max(startScroll + 1, caseTop - window.innerHeight * caseLandingFactor);
  const targetDocCenterX = targetRect.left + window.scrollX + targetRect.width / 2;
  const targetDocCenterY = targetRect.top + window.scrollY + targetRect.height / 2;

  return {
    baseTop: heroTop + modelTop - startScroll,
    baseHeight: modelHeight,
    targetDocCenterX,
    targetDocCenterY,
    targetHeight: targetRect.height,
    startScroll,
    endScroll,
  };
}

function syncTagOverlays(show) {
  caseOverlayTags.forEach((tag) => {
    if (!show) {
      tagOverlays.get(tag)?.remove();
      tagOverlays.delete(tag);
      tag.style.visibility = "";
      return;
    }

    let overlay = tagOverlays.get(tag);
    if (!overlay) {
      overlay = tag.cloneNode(true);
      overlay.setAttribute("aria-hidden", "true");
      overlay.style.pointerEvents = "none";
      overlay.style.visibility = "visible";
      document.body.appendChild(overlay);
      tagOverlays.set(tag, overlay);
    }

    // Hide the in-flow original so only this top overlay layer is visible;
    // it still occupies layout so it can anchor the overlay's position.
    tag.style.visibility = "hidden";

    const rect = tag.getBoundingClientRect();
    const styles = getComputedStyle(tag);
    const layoutWidth = tag.offsetWidth || rect.width;
    const layoutHeight = tag.offsetHeight || rect.height;
    overlay.style.position = "absolute";
    overlay.style.left = `${rect.left + window.scrollX + (rect.width - layoutWidth) / 2}px`;
    overlay.style.top = `${rect.top + window.scrollY + (rect.height - layoutHeight) / 2}px`;
    overlay.style.width = `${layoutWidth}px`;
    overlay.style.height = `${layoutHeight}px`;
    overlay.style.margin = "0";
    overlay.style.zIndex = "80";
    overlay.style.transform = styles.transform;
    overlay.style.transformOrigin = styles.transformOrigin;
  });
}

const heroModelAnchor = heroModel ? document.createComment("hero-model") : null;
if (heroModel && heroModel.parentElement && heroModelAnchor) {
  heroModel.parentElement.insertBefore(heroModelAnchor, heroModel);
}

function setModelInBody(inBody) {
  if (!heroModel || !heroModelAnchor) return;
  const inBodyNow = heroModel.parentElement === document.body;
  if (inBody === inBodyNow) return;
  if (inBody) {
    document.body.appendChild(heroModel);
  } else if (heroModelAnchor.parentElement) {
    heroModelAnchor.parentElement.insertBefore(heroModel, heroModelAnchor);
  }
}

function updateScrollLinkedModel() {
  if (!heroModel || !heroSection || !caseFileSection || !caseModelTarget) {
    scrollMotion.progress = 0;
    scrollMotion.eased = 0;
    scrollMotion.active = false;
    return scrollMotion;
  }

  if (window.matchMedia("(max-width: 900px)").matches) {
    setModelInBody(false);
    heroModel.classList.remove("is-scroll-linked");
    ["position", "left", "right", "top", "width", "height", "transform", "transformOrigin", "zIndex"].forEach((property) => {
      heroModel.style[property] = "";
    });
    syncTagOverlays(false);
    scrollLayout = null;
    scrollMotion.progress = 0;
    scrollMotion.eased = 0;
    scrollMotion.active = false;
    return scrollMotion;
  }

  if (!scrollLayout || !scrollMotion.active) {
    scrollLayout = measureScrollLayout();
  }

  const progress = clamp((window.scrollY - scrollLayout.startScroll) / (scrollLayout.endScroll - scrollLayout.startScroll));
  const eased = easeInOutCubic(progress);
  const isActive = progress > 0.001 || window.scrollY >= scrollLayout.endScroll;

  scrollMotion.progress = progress;
  scrollMotion.eased = eased;
  scrollMotion.active = isActive;

  if (!isActive) {
    setModelInBody(false);
    heroModel.classList.remove("is-scroll-linked");
    heroModel.style.position = "";
    heroModel.style.left = "";
    heroModel.style.right = "";
    heroModel.style.top = "";
    heroModel.style.width = "";
    heroModel.style.height = "";
    heroModel.style.transform = "";
    heroModel.style.transformOrigin = "";
    heroModel.style.zIndex = "";
    syncTagOverlays(false);
    scrollLayout = null;
    return scrollMotion;
  }

  const baseTop = scrollLayout.baseTop;
  const baseHeight = scrollLayout.baseHeight;
  const baseWidth = window.innerWidth;
  const baseCenterX = baseWidth / 2;
  const responsiveMinTargetScale = window.innerWidth < 600 ? 0.2 : window.innerWidth < 900 ? 0.34 : window.innerWidth < 1180 ? 0.48 : 0.72;
  const responsiveTargetBoost = window.innerWidth < 600 ? 2.2 : window.innerWidth < 900 ? 1.7 : window.innerWidth < 1180 ? 1.35 : 1;
  const targetScale = clamp((scrollLayout.targetHeight / baseHeight) * responsiveTargetBoost, responsiveMinTargetScale, 1.08);

  heroModel.classList.add("is-scroll-linked");
  heroModel.style.zIndex = "35";
  heroModel.style.right = "auto";
  heroModel.style.width = `${baseWidth}px`;
  heroModel.style.height = `${baseHeight}px`;
  heroModel.style.transformOrigin = "50% 50%";

  if (progress >= 1) {
    // Landed on screen two: anchor the model in the document (absolute, not
    // fixed) so it scrolls together with the case folder on the compositor.
    // A fixed overlay re-positioned every frame on the main thread lags one
    // frame behind the natively-scrolled background — that was the delay.
    setModelInBody(true);
    heroModel.style.position = "absolute";
    heroModel.style.left = `${scrollLayout.targetDocCenterX - baseWidth / 2}px`;
    heroModel.style.top = `${scrollLayout.targetDocCenterY - baseHeight / 2}px`;
    heroModel.style.transform = `scale(${targetScale})`;
    syncTagOverlays(true);
    return scrollMotion;
  }

  // Travelling from screen one to screen two: follow the scroll as a fixed
  // overlay. Trajectory and speed are unchanged from before.
  setModelInBody(false);
  const baseCenterY = baseTop + baseHeight / 2;
  const targetCenterX = scrollLayout.targetDocCenterX - window.scrollX;
  const targetCenterY = scrollLayout.targetDocCenterY - scrollLayout.endScroll;
  const translateX = (targetCenterX - baseCenterX) * eased;
  const translateY = (targetCenterY - baseCenterY) * eased;
  const scale = lerp(1, targetScale, eased);
  heroModel.style.position = "fixed";
  heroModel.style.left = "0px";
  heroModel.style.top = `${baseTop}px`;
  heroModel.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
  syncTagOverlays(progress > 0.78);

  return scrollMotion;
}

function tick() {
  const now = performance.now();
  tilt.x += (-mouse.y * TILT_PITCH - tilt.x) * 0.08;
  tilt.y += (mouse.x * TILT_YAW - tilt.y) * 0.08;
  const scrollState = updateScrollLinkedModel();

  if (isSwitching) {
    const progress = Math.min((now - flipStart) / FLIP_DURATION, 1);
    const eased = easeInOutCubic(progress);
    root.rotation.y = flipFrom + (flipTo - flipFrom) * eased;

    if (!swappedDuringFlip && progress >= SWAP_PROGRESS) {
      if (activeModel) activeModel.visible = false;
      pendingModel.rotation.y = Math.PI;
      pendingModel.visible = true;
      activeModel = pendingModel;
      if (flavorLabel) flavorLabel.textContent = products[pendingProduct].label;
      swappedDuringFlip = true;
    }

    if (progress >= 1) {
      isSwitching = false;
      currentProduct = pendingProduct;
      pendingProduct = null;
      activeModel.rotation.y = getCaseLocalSpin(scrollState.eased);
      pendingModel = null;
      root.rotation.y = baseRotation.y + tilt.y;
      setButtonsDisabled(false);
    }
  }

  caseRotation.current += (caseRotation.target - caseRotation.current) * 0.12;
  if (Math.abs(caseRotation.target - caseRotation.current) < CASE_ROTATION_EPSILON) {
    caseRotation.current = caseRotation.target;
  }
  if (caseRotation.side === 0 && caseRotation.current >= FULL_TURN_RADIANS - CASE_ROTATION_EPSILON) {
    caseRotation.current = 0;
    caseRotation.target = 0;
  }
  const caseLocalSpin = getCaseLocalSpin(scrollState.eased);

  const isMobileLayout = window.matchMedia("(max-width: 900px)").matches;
  const floatingY = isMobileLayout ? 0.095 : Math.sin(now * 0.0012) * 0.009 + 0.095;
  root.position.y = lerp(floatingY, 0.08, scrollState.eased);
  root.rotation.x = lerp(baseRotation.x + tilt.x, 0, scrollState.eased);
  root.rotation.z = lerp(baseRotation.z - tilt.y * TILT_ROLL, -0.065, scrollState.eased);
  if (!isSwitching) {
    if (activeModel) activeModel.rotation.y = caseLocalSpin;
    const heroYaw = baseRotation.y + tilt.y;
    root.rotation.y = lerp(heroYaw, CASE_SCROLL_ROOT_YAW, scrollState.eased);
  }

  renderer.render(scene, camera);
  requestAnimationFrame(tick);
}

setButtonsDisabled(true);
showInitialProduct().catch((error) => {
  console.error("Could not load pouch model:", error);
  const fallback = document.createElement("div");
  fallback.textContent = "模型加载失败，请检查 Original/Sour GLB 文件";
  fallback.style.cssText =
    "position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);font:700 18px system-ui;color:#17120a;background:#fff7d2;padding:16px 20px;border-radius:999px;";
  container.appendChild(fallback);
});

tick();
